import React, { useState, useEffect, useMemo, useRef } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { CircularProgress } from '@material-ui/core'

import TextField from '../../atoms/inputs/textfield'

import { useTheme } from '@material-ui/core/styles'

import { useSnackbar } from 'notistack'

import messages from '../../../services/messages'

import { setConversations } from '../../../redux/conversations/actions'

import {
  getTabs,
  setTabs,
  getTab,
  setTab,
  tabsError,
} from '../../../redux/tabs/actions'

import { SyncAlt, ExpandMore } from '@material-ui/icons'

import {
  setConversationTabs,
  setConversationTabsStatus,
} from '../../../redux/tabList/actions'

import SelectField from '../../atoms/inputs/selectComponent'
import Button from '../../atoms/inputs/button'
import Tooltip from '../../atoms/display/tooltip'

import useStyles from './DemandStyle'
import { useCallback } from 'react'

import { nanoid } from 'nanoid'

import { setGalleryOpen } from '../../../redux/gallery/actions'
import { setTabulationGallery } from '../../../redux/tabulationGallery/actions'

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core'

const Demand = (props) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const theme = useTheme()
  const dispatch = useDispatch()

  const tabs = useSelector((state) => state.tabs)
  const history = useSelector((state) => state.history)
  const conversations = useSelector(
    (state) => state.conversations.conversations
  )
  const activeConversation = useSelector((state) => state.activeConversation)
  const consumer = useSelector(
    (state) => conversations[activeConversation]?.consumer
  )

  const [skillId, setSkillId] = useState(null)
  const [tabId, setTabId] = useState(null)
  const [fields, setFields] = useState([])
  const [availableTabs, setAvailableTabs] = useState([])
  const [textareaNotes, setTextareaNotes] = useState('')
  const [textareaPrivateNote, setTextareaPrivateNote] = useState('')
  const [loadingTicket, setLoadingTicket] = useState(false)
  const [loadingTabs, setLoadingTabs] = useState(false)
  const [loadingDemandRequest, setLoadingDemandRequest] = useState(false)
  const [tabChildrenMaxLevel, setTabChildrenMaxLevel] = useState(null)

  const tabList = useSelector((state) => state.tabList)
  const [channelExtensions, setChannelExtensions] = useState([])
  const [files, setFiles] = useState([])
  const [images, setImages] = useState([])
  const [maximumSize, setMaximumSize] = useState([])

  const fileInputRef = useRef(null)
  const [isOpen, setIsOpen] = useState([false, false, false])

  const { enqueueSnackbar } = useSnackbar()

  const handleLength = (text) => {
    if (!text.length) {
      return 0
    } else if (text?.length < 10) {
      return -1
    } else if (text?.length > 600) {
      return 1
    }

    return 0
  }

  const loadTabs = async (chatId) => {
    if (chatId) {
      const chat =
        conversations[chatId] ?? history.conversations[consumer?.id]?.[chatId]
      const skill = chat?.skillId
      const tabId = chat?.conversationTab

      if (!tabs.form[skill]) {
        dispatch(getTabs())

        try {
          const response = await messages.get(
            `/tech4h-messages/conversation/tab/${chatId}`
          )

          if (response.status === 200) {
            const data = response?.data?.data?.form?.tabs
            dispatch(setTabs(skill, data))
          } else {
            throw response.data
          }
        } catch (error) {
          let message =
            error?.response?.status === 500
              ? 'Unavailable service'
              : error?.response?.data?.message

          dispatch(tabsError({ action: 'get_tabs', message: message }))
        }
      }

      setSkillId(skill)
      setTabId((prev) => ({ ...prev, [chatId]: tabId }))
    }
  }

  // TODO: make it recursively
  const getRegisteredDemand = (depth) => {
    const chatId = props?.chatId
    let controller = false
    let array = Array(3)
      .fill()
      .map((_) => ({ value: undefined, name: undefined }))

    tabId?.[chatId] &&
      availableTabs?.length > 0 &&
      availableTabs?.forEach((el1) => {
        if (controller) {
          return
        } else {
          array[0] = {
            value: el1.id,
            name: el1.name,
          }

          if (el1.id === tabId?.[chatId]) {
            controller = true
          }
        }

        el1?.children?.forEach((el2) => {
          if (controller) {
            return
          } else {
            array[1] = {
              value: el2.id,
              name: el2.name,
            }
            if (el2.id === tabId?.[chatId]) {
              controller = true
            }
          }

          el2?.children?.forEach((el3) => {
            if (controller) {
              return
            } else {
              array[2] = {
                value: el3.id,
                name: el3.name,
              }
              if (el3.id === tabId?.[chatId]) {
                controller = true
              }
            }
          })
        })
      })

    setFields((prev) => ({ ...prev, [props?.chatId]: array }))

    // Pass props to parent
    props?.isTabulated?.(array.some((el) => el.value))
  }

  const updateDemand = () => {
    const chatId = props?.chatId
    const newTabId = fields?.[chatId]?.[fields[chatId].length - 1]?.value
    const obj = { chatId: chatId, newTabId: newTabId }

    if (chatId && newTabId) {
      conversations[chatId].conversationTab = newTabId
      conversations[chatId].pendingTab = false
      dispatch(setConversations(conversations[chatId]))
      setTabId((prev) => ({ ...prev, [chatId]: newTabId }))
      dispatch(setTab(obj))
      loadTabList(chatId)
    }
  }

  const loadTabList = async (chatId) => {
    if (chatId) {
      dispatch(setConversationTabsStatus('get_tabs', { loading: true }))
      try {
        const res = await messages.get(
          `/tech4h-messages/conversation/tabs/${chatId}`
        )
        if (res.status === 200) {
          const response = res.data.data ?? null
          dispatch(setConversationTabs(chatId, response))
        } else {
          throw res.data
        }
      } catch (error) {
        enqueueSnackbar(t('toasts.actionError'), {
          variant: 'error',
        })
      } finally {
        dispatch(setConversationTabsStatus('get_tabs', { loading: false }))
      }
    }
  }

  const handleTabulateChat = async () => {
    const chatId = props?.chatId
    const tabId =
      fields?.[chatId]?.[2]?.value ||
      fields?.[chatId]?.[1]?.value ||
      fields?.[chatId]?.[0]?.value
    if (props?.ticket) {
      setLoadingTicket(true)
      try {
        const response = await messages.post(
          `/tech4h-messages/response/send/realautomation/${chatId}`,
          {
            tabId: tabId,
            text: textareaNotes,
            privateNote: textareaPrivateNote,
            files: files,
          }
        )
        if (response.status === 200) {
          setLoadingTicket(false)
        } else throw response.data
      } catch (error) {
        console.error('Error', error)
        const message = error?.response?.data?.message ?? 'Unavailable service'
        dispatch(tabsError({ action: 'get_tab', message: message }))
      }
    }
  }

  const registerDemand = async () => {
    const chatId = props?.chatId
    const tabId = fields?.[chatId]?.[fields[chatId].length - 1]?.value

    if (chatId && tabId) {
      dispatch(getTab())
      try {
        const response = await messages.put(
          `/tech4h-messages/conversation/tab/${chatId}`,
          { tab_id: tabId }
        )
        if (response.status === 200) updateDemand()
        else throw response.data
      } catch (error) {
        console.error('Error', error)
        const message = error?.response?.data?.message ?? 'Unavailable service'
        dispatch(tabsError({ action: 'get_tab', message: message }))
      }
    }
  }

  // TODO: it's close to the desired. For now, selects will be static. As soon as possible, refactor backend to allow N demand levels
  const selects = useMemo(() => {
    const chatId = props?.chatId
    /* -------------------------
       TODO: future vars
      --------------------------
       let depth
       let defaultVal
       let
      --------------------------
    */
    const getOptions = (depth) => {
      let result = []
      switch (depth) {
        case 0:
          result = availableTabs?.map((el) => ({ _id: el.id, name: el.name }))
          break
        case 1:
          result = availableTabs
            ?.find((tab) => tab.id === fields?.[chatId]?.[depth - 1]?.value)
            ?.children?.map((el) => ({ _id: el.id, name: el.name }))
          break
        case 2:
          const firstLevel = availableTabs?.find(
            (tab) => tab.id === fields?.[chatId]?.[depth - 2]?.value
          )

          if (firstLevel?.children?.length === 1) {
            result = firstLevel.children[0]?.children?.map((el) => ({
              _id: el.id,
              name: el.name,
            }))
          } else {
            result = availableTabs
              ?.find((tab) => tab.id === fields?.[chatId]?.[depth - 2]?.value)
              ?.children?.find(
                (tab) => tab.id === fields?.[chatId]?.[depth - 1]?.value
              )
              ?.children?.map((el) => ({ _id: el.id, name: el.name }))
          }

          break
        default:
      }

      if (result && (depth == 1 || depth == 2)) {
        result.sort(function (a, b) {
          const textA = a.name.toUpperCase()
          const textB = b.name.toUpperCase()
          return textA < textB ? -1 : textA > textB ? 1 : 0
        })
      }

      return result
    }

    const getValue = (depth) => {
      return fields?.[chatId]?.[depth]?.value
    }

    return [
      {
        name: 'type',
        label: t('fields.type.label'),
        placeholder: t('fields.type.placeHolder'),
        data: getOptions(0),
        value: getValue(0),
        disableAutoselect: true,
      },
      {
        name: 'subject',
        label: t('fields.subject.label'),
        placeholder: t('fields.subject.placeHolder'),
        data: getOptions(1),
        value: getValue(1),
        disableAutoselect: false,
      },
      {
        name: 'subSubject',
        label: t('fields.subSubject.label'),
        placeholder: t('fields.subSubject.placeHolder'),
        data: getOptions(2),
        value: getValue(2),
        disableAutoselect: false,
      },
    ]
  }, [availableTabs, fields, props?.chatId, t])

  const handleItemChange = (e, depth) => {
    const chatId = props?.chatId
    let firstLevel = null
    let secondLevel = null
    let thirdLevel = null

    let newFields = []

    switch (depth) {
      case 0:
        firstLevel = availableTabs?.find((tab) => tab.id === e?.target?.value)
        if (firstLevel?.children?.length === 1)
          secondLevel = firstLevel.children[0]
        if (secondLevel?.children?.length === 1)
          thirdLevel = secondLevel.children[0]
        break
      case 1:
        firstLevel = availableTabs?.find(
          (tab) => tab.id === fields?.[chatId]?.[depth - 1]?.value
        )
        secondLevel = firstLevel?.children?.find(
          (tab) => tab.id === e?.target?.value
        )
        if (secondLevel?.children?.length === 1)
          thirdLevel = secondLevel.children[0]
        break
      case 2:
        firstLevel = availableTabs?.find(
          (tab) => tab.id === fields?.[chatId]?.[depth - 2]?.value
        )
        secondLevel = firstLevel?.children?.find(
          (tab) => tab.id === fields?.[chatId]?.[depth - 1]?.value
        )
        thirdLevel = secondLevel?.children?.find(
          (tab) => tab.id === e?.target?.value
        )
        break
      default:
    }

    if (firstLevel && !Array.isArray(firstLevel))
      newFields[0] = { value: firstLevel.id, name: 'type' }

    if (secondLevel && !Array.isArray(secondLevel))
      newFields[1] = { value: secondLevel.id, name: 'subject' }

    if (thirdLevel && !Array.isArray(thirdLevel))
      newFields[2] = { value: thirdLevel.id, name: 'subSubject' }

    setFields((prev) => ({ ...prev, [props?.chatId]: newFields }))
  }

  const errorNotes = useMemo(() => {
    if (!props?.ticket) {
      return null
    }

    const error = handleLength(textareaNotes)

    if (!error) {
      return false
    }

    if (error === 1) {
      return 'notesMaxLength'
    }

    if (error === -1) {
      return 'notesMinLength'
    }
  }, [props?.ticket, handleLength])

  const errorPrivateNote = useMemo(() => {
    if (!props?.ticket) {
      return null
    }

    const error = handleLength(textareaPrivateNote)

    if (!error) {
      return false
    }

    if (error === 1) {
      return 'notesMaxLength'
    }

    if (error === -1) {
      return 'notesMinLength'
    }
  }, [props?.ticket, handleLength])

  /**
   * Function to "convert" file into base 64
   * @param {Event Element} file
   * @returns file converted to the base 64
   */
  const fileToBase64 = async (file) => {
    const result_base64 = await new Promise((resolve) => {
      const fileReader = new FileReader()
      fileReader.onload = () => resolve(fileReader.result)
      fileReader.readAsDataURL(file)
    })
    return result_base64
  }

  const handleSelectedFiles = useCallback(
    async (event) => {
      let numFiles = event.target.files.length

      if (files?.length + numFiles > 10) {
        enqueueSnackbar(t('fields.attachFiles.errors.amount'), {
          variant: 'error',
        })
        return
      }

      let dataSplit = null

      // Additional loop for checking if all files are below the allowed size
      for (let x = 0; x < numFiles; x++) {
        const FileExt = event?.target?.files?.[x]?.name.split('.').pop()
        let isExtensionAllowed = false

        for (let y = 0; y < channelExtensions?.length; y++) {
          if (channelExtensions[y] === FileExt) {
            isExtensionAllowed = true
          }
        }

        if (isExtensionAllowed === false) {
          enqueueSnackbar(`${t('fields.attachFiles.errors.extension')}`, {
            variant: 'error',
          })
          return
        }

        if (event.target.files[x].size / 1000 > maximumSize) {
          enqueueSnackbar(
            `${t('fields.attachFiles.errors.size')} ${maximumSize / 1000}MB`,
            {
              variant: 'error',
            }
          )
          return
        }
      }

      for (let x = 0; x < numFiles; x++) {
        const id = nanoid()
        const blobURL = URL.createObjectURL(event.target.files[x])
        const preview = new Image()
        const type = event?.target?.files?.[x]?.type?.split('/')?.[0]
        const isImage =
          event?.target?.files?.[x]?.type?.split('/')?.[0] === 'image'
        preview.src = isImage ? blobURL : 'file-thumbnail.png'
        preview.onerror = (error) => {
          URL.revokeObjectURL(blobURL)
        }

        const fileBase64 = await fileToBase64(event?.target?.files[x])
        dataSplit = fileBase64.split(',')

        const fileMimeType = event?.target?.files?.[x]?.type

        const fileType =
          type.split('/')[0] === 'application' ? 'file' : type.split('/')[0]

        const objectToInsert = {
          fileName: event.target.files[x].name,
          id: `${event.target.files[x].name} ${id}`,
          data: dataSplit[1],
          imgTest: preview.src,
          isImage: isImage,
          size: event.target.files[x].size,
          mimeType: fileMimeType,
          type: fileType,
          picture: preview.src,
        }

        setFiles((files) => [...files, objectToInsert])

        if (isImage) {
          setImages((images) => [...images, objectToInsert])
        }
      }
    },
    [files, maximumSize, channelExtensions]
  )

  const removeFile = (files, file) => {
    for (let i = 0; i < files.length; i++) {
      if (files[i] === file) {
        files.splice(i, 1)
        break
      }
    }

    if (file?.isImage) {
      for (let i = 0; i < images.length; i++) {
        if (images[i].id === file.id) {
          images.splice(i, 1)
          break
        }
      }
      setImages(() => [...images])
    }

    setFiles(() => [...files])
  }

  const renderGallery = () => {
    if (images?.length > 0) {
      dispatch(setGalleryOpen(true))
      dispatch(setTabulationGallery(images))
    }
  }

  const renderAccordion = () => {
    let fileSize = files[0]?.size / 1000000

    return (
      <>
        <>
          <div className={classes.fileContainer} style={{ marginBottom: 0 }}>
            <div className={classes.imageAndInfo}>
              <img
                className={`${classes.image} ${
                  files[0]?.isImage && classes.hoverElement
                }`}
                alt={files[0]?.fileName}
                src={files[0]?.imgTest}
                onClick={() => files[0]?.isImage && renderGallery()}
              />
              <div style={{ marginLeft: '1em ' }}>
                <h1 className={classes.fileName}>{files[0]?.fileName}</h1>
                <h2 className={classes.fileSize}>{`${t(
                  'fields.attachFiles.size'
                )} :  ${fileSize.toFixed(3)}MB`}</h2>
              </div>
            </div>
            <div
              className={classes.removeFile}
              onClick={() => {
                removeFile(files, files[0])
              }}
            >
              <h3>{t('fields.attachFiles.remove')}</h3>
            </div>
          </div>
        </>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <h3
              className={classes.chooseButton}
              style={{ margin: '0 0 1em 0' }}
            >
              {`${t('fields.attachFiles.seeOther')} ${files?.length - 1} ${t(
                'fields.attachFiles.files'
              )}`}
            </h3>
          </AccordionSummary>
          <AccordionDetails
            style={{ display: 'flex', flexDirection: 'column', marginTop: 0 }}
          >
            {files?.map((file, idx) => {
              if (idx > 0) {
                fileSize = files[idx]?.size / 1000000
                return (
                  <div className={classes.fileContainer} key={idx}>
                    <div className={classes.imageAndInfo}>
                      <img
                        className={`${classes.image} ${
                          file?.isImage && classes.hoverElement
                        }`}
                        alt={file.fileName}
                        src={file.imgTest}
                        onClick={() => file?.isImage && renderGallery()}
                      />
                      <div style={{ marginLeft: '1em ' }}>
                        <h1 className={classes.fileName}>{file.fileName}</h1>
                        <h2 className={classes.fileSize}>{`${t(
                          'fields.attachFiles.size'
                        )} :  ${fileSize.toFixed(3)}MB`}</h2>
                      </div>
                    </div>
                    <div
                      className={classes.removeFile}
                      onClick={() => {
                        removeFile(files, file)
                      }}
                    >
                      <h3>{t('fields.attachFiles.remove')}</h3>
                    </div>
                  </div>
                )
              }
            })}
          </AccordionDetails>
        </Accordion>
      </>
    )
  }

  const renderFiles = () => {
    if (files?.length > 1) {
      return <>{renderAccordion()}</>
    } else if (files?.length === 1) {
      const fileSize = files[0]?.size / 1000000
      return (
        <>
          <div className={classes.fileContainer} key={0}>
            <div className={classes.imageAndInfo}>
              <img
                className={`${classes.image} ${
                  files[0]?.isImage && classes.hoverElement
                }`}
                alt={files[0]?.fileName}
                src={files[0]?.imgTest}
                onClick={() => files[0]?.isImage && renderGallery()}
              />
              <div style={{ marginLeft: '1em ' }}>
                <h1 className={classes.fileName}>{files[0]?.fileName}</h1>
                <h2 className={classes.fileSize}>{`${t(
                  'fields.attachFiles.size'
                )} :  ${fileSize.toFixed(3)}MB`}</h2>
              </div>
            </div>
            <div
              className={classes.removeFile}
              onClick={() => {
                removeFile(files, files[0])
              }}
            >
              <h3>{t('fields.attachFiles.remove')}</h3>
            </div>
          </div>
        </>
      )
    }
  }

  const renderAttachFiles = useMemo(() => {
    if (renderFiles && conversations) {
      const fileSizeSupported =
        conversations?.[activeConversation]?.supportedFiles?.file?.maxSize /
        1000
      return (
        <div
          style={{
            margin: '0 1em',
          }}
        >
          <h3 className={classes.fileInfo}>{t('fields.attachFiles.info')}</h3>
          <>
            <label
              id="attachfile"
              className={`${classes.hoverElement} m-0 mb-2 hoverCursor`}
              htmlFor="file-input"
              style={{
                height: '32px',
                width: '200px',
                left: '0px',
                top: '29px',
                borderRadius: '4px',
                padding: '4px 12px',
                border: '1px solid #009EE2',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  marginRight: '0.5em',
                  width: '16px',
                  height: '16px',
                  background: '#009EE2',
                  borderRadius: '50%',
                  padding: '0.5em',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <SyncAlt
                  style={{
                    width: '12px',
                    height: '12px',
                    rotate: '90deg',
                    color: 'black',
                  }}
                />
              </div>
              <h3 className={classes.chooseButton}>
                {t('fields.attachFiles.chooseButton')}
              </h3>
            </label>

            <input
              ref={fileInputRef}
              className="d-none"
              multiple
              type="file"
              id="file-input"
              onChange={handleSelectedFiles}
              style={{
                width: '100px',
                height: '100px',
                display: 'none',
              }}
            />
          </>
          <div style={{ margin: '1.5em 0' }}>
            <h5 className={classes.limitText}>
              {`${t('fields.attachFiles.limit')} | ${t(
                'fields.attachFiles.maximumFileSize'
              )} : ${fileSizeSupported.toFixed(2)} MB`}
            </h5>
            <h5 className={classes.limitText}></h5>
            <h5 className={classes.limitText}>
              {`${t('fields.attachFiles.permittedExtension')} ${[
                ...channelExtensions,
              ]}`}
            </h5>
          </div>

          {renderFiles()}
        </div>
      )
    }
  }, [files, t, channelExtensions, conversations, images])

  useEffect(() => {
    if (conversations && props?.ticket) {
      const ext = [
        ...conversations?.[activeConversation]?.supportedFiles?.file?.ext,
        ...conversations?.[activeConversation]?.supportedFiles?.image?.ext,
        ...conversations?.[activeConversation]?.supportedFiles?.video?.ext,
      ]

      setMaximumSize(
        conversations?.[activeConversation]?.supportedFiles?.file?.maxSize
      )

      setChannelExtensions(ext)
    }
  }, [activeConversation, conversations])

  const renderDemand = () => {
    const chatId = props?.chatId
    return (
      <>
        <div className={classes.content} style={{ padding: 5 }}>
          <div width="100%">
            <div
              className={classes.scroll}
              style={{
                maxWidth: props.maxWidth,
              }}
            >
              {selects?.map((el, key) => {
                return (
                  <SelectField
                    selectStyle={classes.select}
                    key={key}
                    name={el.name}
                    label={el.label}
                    isRefDocument={props?.isRefDocument}
                    data={el.data}
                    value={el.value}
                    disableAutoselect={el.disableAutoselect}
                    placeholder={el.placeholder}
                    onChange={(e) => handleItemChange(e, key)}
                    disabled={tabs?.status?.get_tab?.loading}
                  />
                )
              })}
            </div>

            {props?.ticket && (
              <>
                <div style={{ padding: '10px' }}>
                  <TextField
                    id="field-notes"
                    label={t('fields.publicNote.label')}
                    placeholder={t('fields.notes.placeholder')}
                    type="text"
                    color="primary"
                    variant="outlined"
                    name="contactName"
                    value={textareaNotes}
                    rows={4}
                    multiline={true}
                    error={errorNotes}
                    helperText={
                      errorNotes && t(`fields.notes.errors.${errorNotes}`)
                    }
                    shrink={true}
                    required={false}
                    fullWidth={true}
                    onChange={(e) => {
                      setTextareaNotes(e.target.value)
                    }}
                  />
                </div>
                {renderAttachFiles}
                <div style={{ padding: '10px' }}>
                  <TextField
                    id="field-private-notes"
                    label={t('fields.privateNote.label')}
                    placeholder={t('fields.privateNote.placeholder')}
                    type="text"
                    color="primary"
                    variant="outlined"
                    name="privateNote"
                    value={textareaPrivateNote}
                    error={errorPrivateNote}
                    helperText={
                      errorPrivateNote &&
                      t(`fields.notes.errors.${errorPrivateNote}`)
                    }
                    rows={4}
                    multiline={true}
                    shrink={true}
                    required={false}
                    fullWidth={true}
                    onChange={(e) => {
                      setTextareaPrivateNote(e.target.value)
                    }}
                  />
                </div>
              </>
            )}
            {!props?.hideButton && (
              <div style={{ textAlign: 'right', padding: '10px' }}>
                <Button
                  size="large"
                  variant="contained"
                  color={loadingTicket ? 'secundary' : 'primary'}
                  fullWidth={false}
                  onClick={() =>
                    props?.ticket ? handleTabulateChat() : registerDemand()
                  }
                  disabled={
                    !(
                      selects?.length === fields?.[chatId]?.length &&
                      !fields?.[chatId]?.some((el) => !el?.value)
                    ) ||
                    tabs?.status?.get_tab?.loading ||
                    errorNotes ||
                    errorPrivateNote
                  }
                >
                  {tabs?.status?.get_tab?.loading || loadingTicket === true ? (
                    <CircularProgress size={26} color="primary" />
                  ) : !props?.isCloseModal ? (
                    t('buttons.save')
                  ) : (
                    t('buttons.saveAndClose')
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </>
    )
  }

  useEffect(() => {
    loadTabs(props?.chatId)
  }, [props?.chatId])

  useEffect(() => {
    if (skillId) {
      setAvailableTabs(tabs.form[skillId])
    }
  }, [props?.chatId, skillId, tabs?.form])

  useEffect(() => {
    getRegisteredDemand()
  }, [tabId, availableTabs])

  useEffect(() => {
    const chatId = props?.chatId
    const tabId = fields?.[chatId]?.[fields[chatId].length - 1]?.value

    if (props?.hideButton && tabId)
      props?.tabInfo({
        id: tabId,
        active: fields?.[chatId]?.length === 3 ? true : false,
      })
  }, [fields])

  useEffect(() => {
    props?.update && updateDemand()
  }, [props?.update])

  return <>{renderDemand()}</>
}

export default Demand
